import { useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import LeafTabViews, { TabView } from "../../../../common/leaf/LeafTabViews/LeafTabViews";
import { KpiSelectorModal, KpiSelectorProps } from "../../../../common/components/KpiSelectorModal/KpiSelectorModal";
import ReportSelector from "../../ReportSelector/ReportSelector";
import { ActionReportTitlePortal } from "../ActionReportTitlePortal/ActionReportTitlePortal";
import { IconSortFilterV2 } from "../../../../common/icons/IconSortFilterV2/IconSortFilterV2";
import "./ActionReportHeader.scss";
import { useAppSelector } from "../../../../store";
import { useTranslation } from "react-i18next";
import { useCurrentViewExportV2Mutation, useGetExportListV2Query } from "../../../../services/ExportV2/exportV2API";
import { ExportAffABO, ExportDetails, TextOverride } from "../../../../services/ExportV2/exportV2API.types";
import { STATUS_DONE, STATUS_IN_PROGRESS, STATUS_PENDING } from "../../../../common/constants/export-constants";
import { isExportDisabledBySessionSsoOrigin, buildExportFields } from "../../../../common/util/exportUtil";
import { IconDownloadButton } from "../../../../common/icons/IconDownloadButton/IconDownloadButton";
import { IconRotatingCircle } from "../../../../common/icons/IconRotatingCircle/IconRotatingCircle";
import { ActionReportExportModal } from "../../../ServerSideExport/ActionReportExportModal/ActionReportExportModal";
import { ActionReportExportConfirmation } from "../../../ServerSideExport/ActionReportExportModal/ActionReportExportConfirmation";
import { dispatchNotifyInProgressEvent } from "../../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { storedLocale } from "../../../Authentication/util";
import defaultContent from "../../../../content/actionReports";
import serverSideExportContent from "../../../../content/serverSideExport";
import {
  SearchSelect,
  type SearchCallback,
  type SelectionCallback,
} from "../../../../common/components/SearchSelect/SearchSelect";

export type ExportConfig = {
  moduleId: string;
  exportAffAbos: ExportAffABO[];
  selectedKpis: string[];
  confirmationMessage?: string;
  exportType?: string;
  startedByLabels?: Record<string, string>;
  onExportTrack?: () => void;
  fieldTitleOverrides?: Record<string, string>;
  customTriggerExport?: () => Promise<void>;
};

type ExportButtonProps = {
  moduleId: string;
  exportList: ExportDetails[];
  disabled: boolean;
  onClick: () => void;
  exportButtonText: string;
  variant?: "mobile" | "desktop";
};

const ExportButton = ({ moduleId, exportList, disabled, onClick, exportButtonText, variant }: ExportButtonProps) => {
  const showInProgress = exportList.some(
    ({ ExportStatus }) => ExportStatus === STATUS_PENDING || ExportStatus === STATUS_IN_PROGRESS,
  );
  const showDone = exportList.some(({ ExportStatus }) => ExportStatus === STATUS_DONE);

  const testId = variant ? `export-button-${moduleId}-${variant}` : `export-button-${moduleId}`;

  return (
    <button
      type="button"
      className={`ActionReportHeader__exportButton ${disabled ? "ActionReportHeader__exportButton--disabled" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={exportButtonText}
      data-testid={testId}
    >
      <span className="ActionReportHeader__exportButton__iconWrapper">
        {showInProgress ? <IconRotatingCircle /> : <IconDownloadButton width="1rem" height="1rem" />}
      </span>
      <span className="ActionReportHeader__exportButton__text">{exportButtonText}</span>
      {showDone && !showInProgress && !disabled && (
        <span className="ActionReportHeader__exportButton__notificationDot" data-testid="export-notification-dot" />
      )}
    </button>
  );
};

type Props = {
  title: string;
  description: string | React.ReactNode;
  reportLength: number;
  infoButton?: React.ReactNode;
  tabs?: {
    tabViews: TabView[];
    activeView: string;
    setViewOnClick: (activeView: string) => void;
    isLoading?: boolean;
  };
  kpiSelector?: KpiSelectorProps;
  sortFilter?: {
    direction: "asc" | "desc";
    onClick: () => void;
    disabled?: boolean;
  };
  additionalControls?: React.ReactNode;
  searchControl?: React.ReactNode;
  search?: {
    searchCallback: SearchCallback;
    selectionCallback: SelectionCallback;
    searchText: string;
    clearFunction: () => void;
    disabled: boolean;
    onFocus?: () => void;
  };
  hideReportSelector?: boolean;
  showTimeStamp?: boolean;
  export?: ExportConfig;
  exportTrack?: Function;
  isAllEnriched?: boolean;
};

export const ActionReportHeader = ({
  title,
  description,
  reportLength,
  infoButton,
  tabs,
  kpiSelector,
  sortFilter,
  additionalControls,
  searchControl,
  search,
  hideReportSelector = false,
  showTimeStamp,
  export: exportConfig,
  exportTrack = () => {},
  isAllEnriched = true,
}: Props) => {
  const { t } = useTranslation(["actionReports", "serverSideExport"]);
  const boot = useAppSelector((state) => state.boot);
  const { configuration, user } = boot;
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { profile } = configuration;

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isExportListModalOpen, setIsExportListModalOpen] = useState(false);

  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();
  const shouldShowExport = exportConfig && !isExportDisabled;
  const skipExportQuery = !exportConfig?.moduleId || isExportDisabled;

  const [currentViewExport] = useCurrentViewExportV2Mutation();

  const { data: exportListData } = useGetExportListV2Query(
    { moduleId: exportConfig?.moduleId ?? "" },
    { skip: skipExportQuery },
  );

  const exportList = exportListData?.Exports ?? [];
  const hasExistingExports = exportList.length > 0;
  const exportButtonText = t("exportButton", defaultContent.exportButton);

  const hasPendingOrAvailableExports = exportList.some(
    ({ ExportStatus }) =>
      ExportStatus === STATUS_PENDING || ExportStatus === STATUS_IN_PROGRESS || ExportStatus === STATUS_DONE,
  );

  const userAff = user.aff;
  const userAbo = user.abo;
  const userIsoCountryCode = user.isoCountryCode;

  const triggerExport = useCallback(async () => {
    if (!exportConfig || isExportDisabled) return;

    const locale = storedLocale();
    const isoCountryCode = userIsoCountryCode || "";
    const affAbo = `${userAff}-${userAbo}`;
    const { address, addressLinesDivider } = profile?.format || {};

    const textOverride = t("textOverride", {
      returnObjects: true,
      defaultValue: serverSideExportContent.textOverride,
      ns: "serverSideExport",
    }) as TextOverride;

    const exportFields = t("exportFields", {
      returnObjects: true,
      defaultValue: serverSideExportContent.exportFields,
      ns: "serverSideExport",
    }) as Record<string, string>;

    const fields = buildExportFields(exportConfig.selectedKpis, exportFields, exportConfig.fieldTitleOverrides);

    const exportPayload = {
      moduleId: exportConfig.moduleId,
      reportAffAbo: affAbo,
      period: selectedPeriod,
      startedBy: exportConfig.exportType || "",
      locale,
      textOverride,
      fields,
      countryCode: isoCountryCode,
      format: {
        address: {
          pattern: address || "",
          addressLineDivider: addressLinesDivider || "",
        },
      },
      affAbos: exportConfig.exportAffAbos,
      filteredByKPIS: exportConfig.selectedKpis.length > 0,
    };

    try {
      await currentViewExport(exportPayload);
      dispatchNotifyInProgressEvent();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to trigger export for module "${exportConfig.moduleId}":`, error);
    }
  }, [
    // Note: exportConfig is an object literal from the parent, so it's a new reference each render.
    // This implicitly captures changes to exportConfig.fieldTitleOverrides, selectedKpis, etc.
    // If exportConfig is ever memoized, ensure fieldTitleOverrides is in its dependency list.
    exportConfig,
    isExportDisabled,
    profile,
    userAff,
    userAbo,
    userIsoCountryCode,
    selectedPeriod,
    t,
    currentViewExport,
  ]);

  const handleExportButtonClick = () => {
    exportTrack();
    if (exportConfig?.confirmationMessage && !hasPendingOrAvailableExports) {
      setIsConfirmationModalOpen(true);
    } else {
      setIsExportListModalOpen(true);
    }
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleConfirmationConfirm = () => {
    setIsConfirmationModalOpen(false);
    triggerExport();
  };

  const handleExportModalClose = () => {
    setIsExportListModalOpen(false);
  };

  const handleOpenExportModalEvent = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.moduleId === exportConfig?.moduleId && customEvent.detail?.open) {
        setIsExportListModalOpen(true);
      }
    },
    [exportConfig?.moduleId],
  );

  useEffect(() => {
    window.addEventListener("OpenExportV2Modal", handleOpenExportModalEvent);
    return () => {
      window.removeEventListener("OpenExportV2Modal", handleOpenExportModalEvent);
    };
  }, [handleOpenExportModalEvent]);

  const noTabsClass = !tabs ? "ActionReportHeader--noTabs" : "";

  const controlRowRef = useRef<HTMLDivElement>(null);
  const reportSelectorRef = useRef<HTMLDivElement>(null);
  const searchControlRef = useRef<HTMLDivElement>(null);
  const scrollableSectionRef = useRef<HTMLDivElement>(null);
  const filterControlsRef = useRef<HTMLDivElement>(null);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const exportButtonRef = useRef<HTMLDivElement>(null);

  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [hasTabsOverflow, setHasTabsOverflow] = useState(false);

  const breakpointRef = useRef<number | null>(null);
  const lastLayoutChangeRef = useRef<number>(0);

  const LAYOUT_CHECK_DELAY_MS = 100;
  const HYSTERESIS_BUFFER_PX = 40;
  const MIN_LAYOUT_CHANGE_INTERVAL_MS = 150;

  useLayoutEffect(() => {
    let rafId: number;

    const checkLayout = () => {
      if (!controlRowRef.current) return;

      const now = Date.now();

      if (now - lastLayoutChangeRef.current < MIN_LAYOUT_CHANGE_INTERVAL_MS) {
        return;
      }

      const tabsScrollable = scrollableSectionRef.current;
      const isOverflowing = tabsScrollable ? tabsScrollable.scrollWidth > tabsScrollable.clientWidth : false;

      setHasTabsOverflow(isOverflowing);

      const containerParent = controlRowRef.current.parentElement;
      const availableWidth = containerParent?.offsetWidth ?? controlRowRef.current.offsetWidth;

      setIsMobileLayout((currentIsMobile) => {
        let shouldBeMobile: boolean;

        if (currentIsMobile) {
          if (breakpointRef.current !== null) {
            shouldBeMobile = availableWidth < breakpointRef.current + HYSTERESIS_BUFFER_PX;
          } else {
            shouldBeMobile = isOverflowing;
          }
        } else {
          shouldBeMobile = isOverflowing;
          if (shouldBeMobile) {
            breakpointRef.current = availableWidth;
          }
        }

        if (shouldBeMobile !== currentIsMobile) {
          lastLayoutChangeRef.current = now;
        }

        return shouldBeMobile;
      });
    };

    const debouncedCheck = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(checkLayout);
    };

    const initialTimeout = setTimeout(debouncedCheck, LAYOUT_CHECK_DELAY_MS);

    const resizeObserver = new ResizeObserver(debouncedCheck);

    const containerParent = controlRowRef.current?.parentElement;
    if (containerParent) {
      resizeObserver.observe(containerParent);
    }

    return () => {
      clearTimeout(initialTimeout);
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [tabs]);

  useEffect(() => {
    const scrollContainer = scrollableSectionRef.current;
    if (!scrollContainer) return;

    const isTouchDevice = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    if (isTouchDevice) return;

    let isDown = false;
    let isDragging = false;
    let startX: number;
    let scrollLeft: number;

    const DRAG_THRESHOLD = 5;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDown = true;
      isDragging = false;
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      isDragging = false;
      scrollContainer.style.removeProperty("cursor");
    };

    const handleMouseUp = () => {
      isDown = false;
      isDragging = false;
      scrollContainer.style.removeProperty("cursor");
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;

      const x = e.pageX - scrollContainer.offsetLeft;
      const distance = Math.abs(x - startX);

      if (!isDragging && distance > DRAG_THRESHOLD) {
        isDragging = true;
        scrollContainer.style.cursor = "grabbing";
      }

      if (isDragging) {
        e.preventDefault();
        const walk = (x - startX) * 1.5;
        scrollContainer.scrollLeft = scrollLeft - walk;
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    scrollContainer.addEventListener("mousedown", handleMouseDown);
    scrollContainer.addEventListener("mouseleave", handleMouseLeave);
    scrollContainer.addEventListener("mouseup", handleMouseUp);
    scrollContainer.addEventListener("mousemove", handleMouseMove);
    scrollContainer.addEventListener("click", handleClick, true);

    return () => {
      scrollContainer.removeEventListener("mousedown", handleMouseDown);
      scrollContainer.removeEventListener("mouseleave", handleMouseLeave);
      scrollContainer.removeEventListener("mouseup", handleMouseUp);
      scrollContainer.removeEventListener("mousemove", handleMouseMove);
      scrollContainer.removeEventListener("click", handleClick, true);
    };
  }, []);

  const mobileLayoutClass = isMobileLayout ? "ActionReportHeader--mobileLayout" : "";

  return (
    <section
      className={`ActionReportHeader ${noTabsClass} ${mobileLayoutClass} ${showTimeStamp ? "ActionReportHeader--timestamp" : ""} `}
    >
      {!hideReportSelector ? (
        <ActionReportTitlePortal
          title={title}
          infoButton={infoButton}
          className="ActionReportHeader__titleHeader ActionReportHeader__titleHeader--withSelector"
          showTimeStamp={showTimeStamp}
        />
      ) : (
        <div className="ActionReportHeader__titleHeader ActionReportHeader__titleHeader--simple">
          <h1 className="ActionReportHeader__title" data-testid="action-report-title">
            {title}
          </h1>
          {infoButton}
        </div>
      )}
      {description && (
        <div className="ActionReportHeader__container">
          <div className="ActionReportHeader__description" data-testid="action-report-description">
            <p>{description}</p>
          </div>
        </div>
      )}
      {search && (
        <div className="ActionReportHeader__searchRow" onFocusCapture={search.onFocus}>
          <SearchSelect
            placeholderText={t("searchPlaceholder", defaultContent["searchPlaceholder"]).toString()}
            clearSearchAriaLabel={t("clearSearch", defaultContent["clearSearch"]).toString()}
            searchCallback={search.searchCallback}
            selectionCallback={search.selectionCallback}
            disabled={search.disabled}
            searchText={search.searchText}
            clear={search.clearFunction}
          />
        </div>
      )}
      {shouldShowExport && (
        <>
          <ActionReportExportConfirmation
            open={isConfirmationModalOpen}
            onCancel={handleConfirmationCancel}
            onConfirm={handleConfirmationConfirm}
            reportDescription={exportConfig.confirmationMessage ?? ""}
          />
          <ActionReportExportModal
            moduleId={exportConfig.moduleId}
            selectedKpis={exportConfig.selectedKpis}
            affAbos={exportConfig.exportAffAbos}
            startedBy={exportConfig.exportType || ""}
            startedByLabels={exportConfig.startedByLabels}
            fieldTitleOverrides={exportConfig.fieldTitleOverrides}
            open={isExportListModalOpen}
            onCloseCallbackFn={handleExportModalClose}
          />
        </>
      )}
    </section>
  );
};
